import React, { useState } from "react";
import api from "@/lib/api";
import { toast } from "sonner";
import { useContent } from "@/components/PublicLayout";
import { Zap, Copy, Save, Plus, Trash2, Car, Home as HomeIcon } from "lucide-react";

const inp = "w-full h-10 px-3 rounded-sm border border-slate-300 text-sm focus:outline-none focus:ring-2 focus:ring-[#C5A059]";

// Field lists used to score completeness for a live EZLynx auto/home quote.
const REQ_AUTO_APPLICANT = ["first_name", "last_name", "dob", "address", "city", "state", "zip", "marital_status"];
const REQ_DRIVER = ["name", "dob", "gender", "license_number", "license_state", "license_status"];
const REQ_VEHICLE = ["year", "make", "model", "vin", "usage", "garaging_zip"];
const REQ_AUTO_COV = ["bi_pd_limits", "comp_deductible", "coll_deductible", "effective_date"];
const REQ_HOME = ["first_name", "last_name", "dob", "address", "city", "state", "zip",
  "year_built", "construction", "sqft", "roof_type", "roof_age", "occupancy", "cov_a", "deductible", "effective_date"];

function deriveFromAnswers(lead) {
  const a = lead.answers || {};
  const isHome = (lead.insurance_types || []).some((t) => /home|renter/i.test(t));
  const lob = isHome ? "home" : "auto";
  const base = {
    lob,
    first_name: lead.first_name || "", last_name: lead.last_name || "",
    phone: lead.phone || "", email: lead.email || "",
    dob: a.dob || "", marital_status: a.marital_status || "", gender: a.gender || "",
    address: a.address || lead.address || "", city: a.city || "", state: a.state || "PA", zip: lead.zip_code || a.zip || "",
    current_carrier: a.current_carrier || lead.current_carrier || "", current_premium: lead.current_premium || "",
    prior_expiration: a.expiration || "", years_with_carrier: a.years_with_carrier || "", coverage_lapse: a.coverage_lapse || "No",
    // auto
    drivers: [{ name: `${lead.first_name || ""} ${lead.last_name || ""}`.trim(), dob: a.dob || "", gender: a.gender || "", marital_status: a.marital_status || "", license_number: "", license_state: "PA", license_status: a.license_status || "Valid", incidents: a.accidents || "None" }],
    vehicles: [{ year: a.vehicle_year || "", make: a.vehicle_make || "", model: a.vehicle_model || "", vin: "", ownership: "Owned", usage: "Commute", annual_mileage: "", garaging_zip: lead.zip_code || "" }],
    bi_pd_limits: a.coverage_level || "", um_uim: "", comp_deductible: "", coll_deductible: "", med_pay: "", towing: "No", rental: "No", effective_date: "",
    // home
    year_built: a.year_built || "", construction: "Frame", sqft: a.sq_ft || "", stories: "", roof_type: "", roof_age: "",
    occupancy: a.home_type || "Primary", protection_class: "", updates: "", prior_claims: "None", pool: "No", trampoline: "No", dog_breed: "", mortgagee: "",
    cov_a: "", cov_c: "", cov_d: "", cov_e: "", cov_f: "", deductible: "", wind_hail_deductible: "",
  };
  return base;
}

function scoreCompleteness(r) {
  let req = [];
  if (r.lob === "home") req = REQ_HOME;
  else req = [...REQ_AUTO_APPLICANT, ...REQ_AUTO_COV];
  const flat = { ...r };
  let filled = req.filter((k) => flat[k] && String(flat[k]).trim()).length;
  let total = req.length;
  if (r.lob === "auto") {
    (r.drivers || []).forEach((d) => { total += REQ_DRIVER.length; filled += REQ_DRIVER.filter((k) => d[k] && String(d[k]).trim()).length; });
    (r.vehicles || []).forEach((v) => { total += REQ_VEHICLE.length; filled += REQ_VEHICLE.filter((k) => v[k] && String(v[k]).trim()).length; });
  }
  return Math.round((filled / Math.max(total, 1)) * 100);
}

function buildText(r) {
  const L = [];
  L.push("=== KEYSTONE SHIELD — EZLYNX RATING DATA ===");
  L.push(`Line of Business: ${r.lob === "home" ? "Homeowners" : "Personal Auto"}`);
  L.push("");
  L.push("[ APPLICANT ]");
  L.push(`Name: ${r.first_name} ${r.last_name}`);
  L.push(`DOB: ${r.dob}   Gender: ${r.gender}   Marital: ${r.marital_status}`);
  L.push(`Phone: ${r.phone}   Email: ${r.email}`);
  L.push(`Address: ${r.address}, ${r.city}, ${r.state} ${r.zip}`);
  L.push("");
  L.push("[ CURRENT INSURANCE ]");
  L.push(`Carrier: ${r.current_carrier}   Premium: ${r.current_premium}`);
  L.push(`Expiration: ${r.prior_expiration}   Years w/ carrier: ${r.years_with_carrier}   Lapse: ${r.coverage_lapse}`);
  L.push("");
  if (r.lob === "auto") {
    (r.drivers || []).forEach((d, i) => {
      L.push(`[ DRIVER ${i + 1} ]`);
      L.push(`Name: ${d.name}   DOB: ${d.dob}   Gender: ${d.gender}   Marital: ${d.marital_status}`);
      L.push(`License #: ${d.license_number}   State: ${d.license_state}   Status: ${d.license_status}   Incidents: ${d.incidents}`);
      L.push("");
    });
    (r.vehicles || []).forEach((v, i) => {
      L.push(`[ VEHICLE ${i + 1} ]`);
      L.push(`${v.year} ${v.make} ${v.model}   VIN: ${v.vin}`);
      L.push(`Ownership: ${v.ownership}   Usage: ${v.usage}   Annual Miles: ${v.annual_mileage}   Garaging ZIP: ${v.garaging_zip}`);
      L.push("");
    });
    L.push("[ COVERAGE REQUESTED ]");
    L.push(`BI/PD: ${r.bi_pd_limits}   UM/UIM: ${r.um_uim}   Med Pay: ${r.med_pay}`);
    L.push(`Comp Ded: ${r.comp_deductible}   Coll Ded: ${r.coll_deductible}   Towing: ${r.towing}   Rental: ${r.rental}`);
    L.push(`Effective Date: ${r.effective_date}`);
  } else {
    L.push("[ PROPERTY ]");
    L.push(`Year Built: ${r.year_built}   Construction: ${r.construction}   Sq Ft: ${r.sqft}   Stories: ${r.stories}`);
    L.push(`Roof: ${r.roof_type} (age ${r.roof_age})   Occupancy: ${r.occupancy}   Protection Class: ${r.protection_class}`);
    L.push(`Updates: ${r.updates}   Prior Claims: ${r.prior_claims}`);
    L.push(`Pool: ${r.pool}   Trampoline: ${r.trampoline}   Dog: ${r.dog_breed}   Mortgagee: ${r.mortgagee}`);
    L.push("");
    L.push("[ COVERAGE REQUESTED ]");
    L.push(`Cov A: ${r.cov_a}   Cov C: ${r.cov_c}   Cov D: ${r.cov_d}   Cov E: ${r.cov_e}   Cov F: ${r.cov_f}`);
    L.push(`Deductible: ${r.deductible}   Wind/Hail: ${r.wind_hail_deductible}   Effective Date: ${r.effective_date}`);
  }
  return L.join("\n");
}

const F = ({ label, val, onChange, type = "text", options, tid }) => (
  <div>
    <label className="text-[11px] uppercase tracking-wider text-slate-500 font-semibold">{label}</label>
    {options ? (
      <select className={inp} value={val || ""} onChange={(e) => onChange(e.target.value)} data-testid={tid}>
        <option value="">—</option>{options.map((o) => <option key={o} value={o}>{o}</option>)}
      </select>
    ) : (
      <input type={type} className={inp} value={val || ""} onChange={(e) => onChange(e.target.value)} data-testid={tid} />
    )}
  </div>
);

export default function EzlynxRating({ leadId, lead }) {
  const content = useContent();
  const [r, setR] = useState(lead.rating || deriveFromAnswers(lead));
  const [open, setOpen] = useState(false);
  const set = (k, v) => setR({ ...r, [k]: v });
  const setArr = (key, i, k, v) => { const arr = [...r[key]]; arr[i] = { ...arr[i], [k]: v }; setR({ ...r, [key]: arr }); };
  const addRow = (key, tmpl) => setR({ ...r, [key]: [...(r[key] || []), tmpl] });
  const delRow = (key, i) => setR({ ...r, [key]: r[key].filter((_, x) => x !== i) });
  const pct = scoreCompleteness(r);

  const save = async () => { await api.patch(`/leads/${leadId}/rating`, { rating: r }); toast.success("Rating data saved"); };
  const copy = async () => { await navigator.clipboard.writeText(buildText(r)); toast.success("EZLynx data copied to clipboard"); };
  const rate = async () => {
    await api.patch(`/leads/${leadId}/rating`, { rating: r });
    try { await navigator.clipboard.writeText(buildText(r)); } catch (e) {}
    await api.post(`/leads/${leadId}/ezlynx-bridge`);
    window.open(content?.ezlynx_url || "https://app.ezlynx.com/", "_blank", "noopener");
    toast.success("Data copied & EZLynx opened. Paste into the applicant to rate.");
  };

  return (
    <div className="bg-white border border-slate-200 rounded-sm p-5" data-testid="ezlynx-panel">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div className="flex items-center gap-2">
          {r.lob === "home" ? <HomeIcon size={18} className="text-[#C5A059]" /> : <Car size={18} className="text-[#C5A059]" />}
          <h3 className="font-display text-lg font-bold">EZLynx Rating Data</h3>
        </div>
        <div className="flex items-center gap-2">
          <div className="text-right">
            <p className="text-[11px] uppercase tracking-wider text-slate-500">Ready to rate</p>
            <p className={`font-mono font-bold ${pct >= 80 ? "text-green-600" : pct >= 50 ? "text-[#C5A059]" : "text-red-500"}`} data-testid="ezlynx-completeness">{pct}%</p>
          </div>
        </div>
      </div>
      <div className="h-1.5 bg-slate-200 rounded-full mt-2 overflow-hidden">
        <div className={`h-full transition-all ${pct >= 80 ? "bg-green-600" : "bg-[#C5A059]"}`} style={{ width: `${pct}%` }} />
      </div>

      <div className="flex flex-wrap gap-2 mt-4">
        <select className={`${inp} w-40`} value={r.lob} onChange={(e) => set("lob", e.target.value)} data-testid="ezlynx-lob">
          <option value="auto">Personal Auto</option><option value="home">Homeowners</option>
        </select>
        <button onClick={rate} className="bg-[#0B1E36] text-white font-bold px-4 h-10 rounded-sm flex items-center gap-2 text-sm hover:bg-[#C5A059] hover:text-[#0B1E36] transition-colors" data-testid="rate-in-ezlynx"><Zap size={15} /> RATE IN EZLYNX</button>
        <button onClick={copy} className="border border-slate-300 px-4 h-10 rounded-sm flex items-center gap-2 text-sm font-semibold" data-testid="ezlynx-copy"><Copy size={15} /> Copy Data</button>
        <button onClick={save} className="border border-slate-300 px-4 h-10 rounded-sm flex items-center gap-2 text-sm font-semibold" data-testid="ezlynx-save"><Save size={15} /> Save</button>
        <button onClick={() => setOpen(!open)} className="text-sm font-semibold text-[#C5A059] px-2" data-testid="ezlynx-toggle">{open ? "Hide details" : "Edit rating details"}</button>
      </div>
      <p className="text-xs text-slate-500 mt-2">One-click bridge: saves data, copies a paste-ready block, and opens EZLynx so your agent rates without retyping. (Full API auto-push activates once EZLynx Connect credentials are provisioned.)</p>

      {open && (
        <div className="mt-5 space-y-5">
          <div>
            <p className="text-xs uppercase tracking-wider font-bold text-[#0B1E36] mb-2">Applicant</p>
            <div className="grid sm:grid-cols-3 gap-3">
              <F label="First Name" val={r.first_name} onChange={(v) => set("first_name", v)} tid="ez-first" />
              <F label="Last Name" val={r.last_name} onChange={(v) => set("last_name", v)} tid="ez-last" />
              <F label="DOB" type="date" val={r.dob} onChange={(v) => set("dob", v)} tid="ez-dob" />
              <F label="Gender" val={r.gender} onChange={(v) => set("gender", v)} options={["Male", "Female", "Other"]} />
              <F label="Marital Status" val={r.marital_status} onChange={(v) => set("marital_status", v)} options={["Single", "Married", "Divorced", "Widowed"]} />
              <F label="Address" val={r.address} onChange={(v) => set("address", v)} />
              <F label="City" val={r.city} onChange={(v) => set("city", v)} />
              <F label="State" val={r.state} onChange={(v) => set("state", v)} options={["PA", "NJ", "NY", "FL"]} />
              <F label="ZIP" val={r.zip} onChange={(v) => set("zip", v)} />
            </div>
          </div>

          <div>
            <p className="text-xs uppercase tracking-wider font-bold text-[#0B1E36] mb-2">Current Insurance</p>
            <div className="grid sm:grid-cols-3 gap-3">
              <F label="Carrier" val={r.current_carrier} onChange={(v) => set("current_carrier", v)} />
              <F label="Expiration" type="date" val={r.prior_expiration} onChange={(v) => set("prior_expiration", v)} />
              <F label="Years w/ Carrier" val={r.years_with_carrier} onChange={(v) => set("years_with_carrier", v)} />
              <F label="Coverage Lapse" val={r.coverage_lapse} onChange={(v) => set("coverage_lapse", v)} options={["No", "Yes"]} />
            </div>
          </div>

          {r.lob === "auto" ? (
            <>
              <div>
                <div className="flex items-center justify-between mb-2"><p className="text-xs uppercase tracking-wider font-bold text-[#0B1E36]">Drivers</p><button onClick={() => addRow("drivers", { name: "", dob: "", gender: "", marital_status: "", license_number: "", license_state: "PA", license_status: "Valid", incidents: "None" })} className="text-xs font-bold text-[#C5A059] flex items-center gap-1" data-testid="ez-add-driver"><Plus size={13} /> Add Driver</button></div>
                {(r.drivers || []).map((d, i) => (
                  <div key={i} className="border border-slate-200 rounded-sm p-3 mb-2">
                    <div className="flex justify-between mb-2"><span className="text-xs font-semibold">Driver {i + 1}</span>{r.drivers.length > 1 && <button onClick={() => delRow("drivers", i)} data-testid={`ez-del-driver-${i}`}><Trash2 size={14} className="text-red-500" /></button>}</div>
                    <div className="grid sm:grid-cols-4 gap-2">
                      <F label="Name" val={d.name} onChange={(v) => setArr("drivers", i, "name", v)} />
                      <F label="DOB" type="date" val={d.dob} onChange={(v) => setArr("drivers", i, "dob", v)} />
                      <F label="Gender" val={d.gender} onChange={(v) => setArr("drivers", i, "gender", v)} options={["Male", "Female", "Other"]} />
                      <F label="Marital" val={d.marital_status} onChange={(v) => setArr("drivers", i, "marital_status", v)} options={["Single", "Married", "Divorced", "Widowed"]} />
                      <F label="License #" val={d.license_number} onChange={(v) => setArr("drivers", i, "license_number", v)} />
                      <F label="Lic. State" val={d.license_state} onChange={(v) => setArr("drivers", i, "license_state", v)} options={["PA", "NJ", "NY", "FL"]} />
                      <F label="Lic. Status" val={d.license_status} onChange={(v) => setArr("drivers", i, "license_status", v)} options={["Valid", "Suspended", "Permit", "New Driver"]} />
                      <F label="Incidents (3yr)" val={d.incidents} onChange={(v) => setArr("drivers", i, "incidents", v)} options={["None", "1 Accident", "1 Violation", "2+"]} />
                    </div>
                  </div>
                ))}
              </div>
              <div>
                <div className="flex items-center justify-between mb-2"><p className="text-xs uppercase tracking-wider font-bold text-[#0B1E36]">Vehicles</p><button onClick={() => addRow("vehicles", { year: "", make: "", model: "", vin: "", ownership: "Owned", usage: "Commute", annual_mileage: "", garaging_zip: r.zip })} className="text-xs font-bold text-[#C5A059] flex items-center gap-1" data-testid="ez-add-vehicle"><Plus size={13} /> Add Vehicle</button></div>
                {(r.vehicles || []).map((v, i) => (
                  <div key={i} className="border border-slate-200 rounded-sm p-3 mb-2">
                    <div className="flex justify-between mb-2"><span className="text-xs font-semibold">Vehicle {i + 1}</span>{r.vehicles.length > 1 && <button onClick={() => delRow("vehicles", i)} data-testid={`ez-del-vehicle-${i}`}><Trash2 size={14} className="text-red-500" /></button>}</div>
                    <div className="grid sm:grid-cols-4 gap-2">
                      <F label="Year" val={v.year} onChange={(x) => setArr("vehicles", i, "year", x)} />
                      <F label="Make" val={v.make} onChange={(x) => setArr("vehicles", i, "make", x)} />
                      <F label="Model" val={v.model} onChange={(x) => setArr("vehicles", i, "model", x)} />
                      <F label="VIN" val={v.vin} onChange={(x) => setArr("vehicles", i, "vin", x)} />
                      <F label="Ownership" val={v.ownership} onChange={(x) => setArr("vehicles", i, "ownership", x)} options={["Owned", "Financed", "Leased"]} />
                      <F label="Usage" val={v.usage} onChange={(x) => setArr("vehicles", i, "usage", x)} options={["Commute", "Pleasure", "Business", "Farm"]} />
                      <F label="Annual Miles" val={v.annual_mileage} onChange={(x) => setArr("vehicles", i, "annual_mileage", x)} />
                      <F label="Garaging ZIP" val={v.garaging_zip} onChange={(x) => setArr("vehicles", i, "garaging_zip", x)} />
                    </div>
                  </div>
                ))}
              </div>
              <div>
                <p className="text-xs uppercase tracking-wider font-bold text-[#0B1E36] mb-2">Coverage Requested</p>
                <div className="grid sm:grid-cols-4 gap-3">
                  <F label="BI/PD Limits" val={r.bi_pd_limits} onChange={(v) => set("bi_pd_limits", v)} options={["State Minimum", "50/100/50", "100/300/100", "250/500/100", "Full Coverage"]} />
                  <F label="UM/UIM" val={r.um_uim} onChange={(v) => set("um_uim", v)} options={["Reject", "50/100", "100/300", "250/500"]} />
                  <F label="Comp Ded." val={r.comp_deductible} onChange={(v) => set("comp_deductible", v)} options={["No Coverage", "250", "500", "1000"]} />
                  <F label="Coll Ded." val={r.coll_deductible} onChange={(v) => set("coll_deductible", v)} options={["No Coverage", "250", "500", "1000"]} />
                  <F label="Med Pay" val={r.med_pay} onChange={(v) => set("med_pay", v)} options={["None", "5000", "10000", "25000"]} />
                  <F label="Towing" val={r.towing} onChange={(v) => set("towing", v)} options={["No", "Yes"]} />
                  <F label="Rental" val={r.rental} onChange={(v) => set("rental", v)} options={["No", "Yes"]} />
                  <F label="Effective Date" type="date" val={r.effective_date} onChange={(v) => set("effective_date", v)} />
                </div>
              </div>
            </>
          ) : (
            <>
              <div>
                <p className="text-xs uppercase tracking-wider font-bold text-[#0B1E36] mb-2">Property</p>
                <div className="grid sm:grid-cols-4 gap-3">
                  <F label="Year Built" val={r.year_built} onChange={(v) => set("year_built", v)} />
                  <F label="Construction" val={r.construction} onChange={(v) => set("construction", v)} options={["Frame", "Masonry", "Brick", "Stucco"]} />
                  <F label="Sq Ft" val={r.sqft} onChange={(v) => set("sqft", v)} />
                  <F label="Stories" val={r.stories} onChange={(v) => set("stories", v)} options={["1", "1.5", "2", "3+"]} />
                  <F label="Roof Type" val={r.roof_type} onChange={(v) => set("roof_type", v)} options={["Asphalt Shingle", "Metal", "Tile", "Slate", "Flat"]} />
                  <F label="Roof Age (yrs)" val={r.roof_age} onChange={(v) => set("roof_age", v)} />
                  <F label="Occupancy" val={r.occupancy} onChange={(v) => set("occupancy", v)} options={["Primary", "Secondary", "Rental", "Renter"]} />
                  <F label="Protection Class" val={r.protection_class} onChange={(v) => set("protection_class", v)} />
                  <F label="Prior Claims" val={r.prior_claims} onChange={(v) => set("prior_claims", v)} options={["None", "1", "2+"]} />
                  <F label="Pool" val={r.pool} onChange={(v) => set("pool", v)} options={["No", "Yes"]} />
                  <F label="Trampoline" val={r.trampoline} onChange={(v) => set("trampoline", v)} options={["No", "Yes"]} />
                  <F label="Dog Breed" val={r.dog_breed} onChange={(v) => set("dog_breed", v)} />
                  <F label="Updates (roof/elec/plumb yr)" val={r.updates} onChange={(v) => set("updates", v)} />
                  <F label="Mortgagee" val={r.mortgagee} onChange={(v) => set("mortgagee", v)} />
                </div>
              </div>
              <div>
                <p className="text-xs uppercase tracking-wider font-bold text-[#0B1E36] mb-2">Coverage Requested</p>
                <div className="grid sm:grid-cols-4 gap-3">
                  <F label="Cov A (Dwelling)" val={r.cov_a} onChange={(v) => set("cov_a", v)} />
                  <F label="Cov C (Personal Prop.)" val={r.cov_c} onChange={(v) => set("cov_c", v)} />
                  <F label="Cov D (Loss of Use)" val={r.cov_d} onChange={(v) => set("cov_d", v)} />
                  <F label="Cov E (Liability)" val={r.cov_e} onChange={(v) => set("cov_e", v)} options={["100000", "300000", "500000"]} />
                  <F label="Cov F (Med Pay)" val={r.cov_f} onChange={(v) => set("cov_f", v)} options={["1000", "5000"]} />
                  <F label="Deductible" val={r.deductible} onChange={(v) => set("deductible", v)} options={["500", "1000", "2500", "5000"]} />
                  <F label="Wind/Hail Ded." val={r.wind_hail_deductible} onChange={(v) => set("wind_hail_deductible", v)} />
                  <F label="Effective Date" type="date" val={r.effective_date} onChange={(v) => set("effective_date", v)} />
                </div>
              </div>
            </>
          )}
        </div>
      )}
    </div>
  );
}
